import React, { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import ProfilePicture from "../util/ProfilePicture";
import { getLeaderboardWeekLabel } from "./memoryGameDates";

function formatElapsed(totalSeconds) {
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

function getSharedRank(entries, index, metric) {
  const value = Number(entries[index]?.[metric]);
  const firstMatchingIndex = entries.findIndex((entry) => Number(entry?.[metric]) === value);
  return firstMatchingIndex + 1;
}

export default function LeaderboardSection({ leaderboards, currentUserId, weekLabel }) {
  const [view, setView] = useState("weekly");
  const [history, setHistory] = useState(null);
  const [status, setStatus] = useState("idle");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (view === "weekly") return undefined;
    let active = true;
    setStatus("loading");
    httpsCallable(getFunctions(), "getMemoryGameHistory")({}).then((result) => {
      if (active) { setHistory(result.data); setStatus("ready"); }
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [view, currentUserId, retry]);
  const previous = view === "previous";
  const shownBoards = previous ? history?.previousWeek?.leaderboards : leaderboards;

  return (
    <section className="mt-5 px-2 sm:px-0" aria-labelledby="weekly-leaderboards-title">
      <div className="mb-3 text-center">
        <h2 id="weekly-leaderboards-title" className="mb-1 text-xl font-extrabold text-slate-800 md:text-2xl">
          Leaderboards
        </h2>
        <p className="m-0 text-xs font-medium text-slate-500">Play each week. Build your record.</p>
        <div className="mt-3 inline-flex max-w-full rounded-full bg-slate-100 p-1" role="group" aria-label="Leaderboard view">
          {[["weekly", "This week"], ["allTime", "Top-five finishes"]].map(([value, label]) => (
            <button key={value} type="button" aria-pressed={value === "weekly" ? view !== "allTime" : view === value}
              onClick={() => setView(value)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                (value === "weekly" ? view !== "allTime" : view === value) ? "bg-white text-slate-800 shadow-sm" : "bg-transparent text-slate-500 hover:text-slate-800"
              }`}>{label}</button>
          ))}
        </div>
      </div>

      {view !== "allTime" && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
          <p className="m-0 text-xs text-slate-500">{previous ? (history?.previousWeek ? getLeaderboardWeekLabel(history.previousWeek.periodEnd) : "Previous week") : weekLabel} · {previous ? "Final results" : "Your best result counts"}</p>
          <button type="button" onClick={() => setView(previous ? "weekly" : "previous")}
            className="bg-transparent text-xs font-semibold text-orange-700 underline underline-offset-4">
            {previous ? "Back to this week" : "Previous week"}
          </button>
        </div>
      )}
      {view !== "weekly" && status === "loading" && <p role="status" className="py-8 text-center text-sm text-slate-500">Loading results…</p>}
      {view !== "weekly" && status === "error" && <div role="alert" className="py-8 text-center text-sm text-slate-500">
        <p>We couldn’t load these results.</p>
        <button type="button" onClick={() => setRetry((value) => value + 1)} className="rounded-full bg-white px-4 py-2 font-bold text-orange-700">Try again</button>
      </div>}
      {view === "allTime" && status === "ready" && <div>
        <p className="mb-4 text-center text-xs leading-relaxed text-slate-500">
          Times finished in the weekly top five. Moves and time count separately; finishing in both earns one in each. Ties count. The current week is still in play.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {[["moves", "movesFinishes", "🧠", "Fewest Moves"], ["time", "timeFinishes", "⚡", "Fastest Time"]].map(([category, metric, icon, title]) => {
            const entries = history.leaderboards?.[category] || [];
            const personalCount = history.personal?.[metric] || 0;
            return <div key={category}>
              <LeaderboardCard icon={icon} title={title} entries={entries} metric={metric} currentUserId={currentUserId}
                emptyMessage="The first top-five finishes will appear when this week closes." />
              {personalCount > 0 && !entries.some((entry) => entry.uid === currentUserId) && <p className="mt-3 text-center text-sm text-slate-600">Your record: <strong>{personalCount} {personalCount === 1 ? "time" : "times"}</strong></p>}
            </div>;
          })}
        </div>
        {history.historyStartedBoardId && <p className="mt-3 text-center text-xs text-slate-400">Tracking from the week of {history.historyStartedBoardId}. Showing up to 100 players per leaderboard.</p>}
      </div>}
      {previous && status === "ready" && !shownBoards && <p className="rounded-2xl border border-slate-200 bg-white px-5 py-9 text-center text-sm text-slate-500">Earlier weekly results weren’t archived. This week’s final results will be saved when the next board opens.</p>}
      {(view === "weekly" || (previous && status === "ready" && shownBoards)) && <div className="grid gap-4 md:grid-cols-2">
        <LeaderboardCard
          icon="🧠"
          title="Fewest Moves"
          key={`${view}-moves`}
          entries={shownBoards.moves}
          metric="moves"
          currentUserId={currentUserId}
        />
        <LeaderboardCard
          icon="⚡"
          title="Fastest Time"
          key={`${view}-time`}
          entries={shownBoards.time}
          metric="elapsedSeconds"
          currentUserId={currentUserId}
        />
      </div>}
    </section>
  );
}

function LeaderboardCard({ icon, title, entries, metric, currentUserId, emptyMessage = "Complete the game to claim the first spot." }) {
  const [visibleCount, setVisibleCount] = useState(10);
  const visibleEntries = entries.slice(0, visibleCount);
  const currentUserIndex = entries.findIndex((entry) => entry.uid === currentUserId);
  const showPinnedUser = currentUserIndex >= visibleCount;
  const canLoadMore = visibleCount < entries.length;
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <span className="text-xl" aria-hidden="true">{icon}</span>
        <h3 className="m-0 text-base font-extrabold text-slate-800">{title}</h3>
      </div>

      {visibleEntries.length === 0 ? (
        <div className="px-5 py-9 text-center text-sm text-slate-500">
          {emptyMessage}
        </div>
      ) : (
        <ol className="m-0 list-none p-0">
          {visibleEntries.map((entry, index) => {
            const rank = getSharedRank(entries, index, metric);
            return (
              <LeaderboardRow
                key={entry.uid}
                entry={entry}
                rank={rank}
                metric={metric}
                isCurrentUser={entry.uid === currentUserId}
              />
            );
          })}
        </ol>
      )}

      {canLoadMore && (
        <button
          type="button"
          onClick={() => setVisibleCount((count) => Math.min(count + 10, entries.length))}
          className="block w-full border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-bold text-slate-500 transition hover:bg-orange-50 hover:text-orange-600"
        >
          Load 10 more
        </button>
      )}

      {showPinnedUser && (
        <div className="border-t-2 border-dashed border-orange-200 bg-orange-50/50 pt-1">
          <p className="mb-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-500">Your position</p>
          <LeaderboardRow
            entry={entries[currentUserIndex]}
            rank={getSharedRank(entries, currentUserIndex, metric)}
            metric={metric}
            isCurrentUser={true}
            as="div"
          />
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ entry, rank, metric, isCurrentUser, as: Component = "li" }) {
  return (
    <Component
      className={`grid grid-cols-[2rem_2.25rem_minmax(0,1fr)_auto] items-center gap-2 border-b border-slate-100 px-3 py-2.5 last:border-b-0 ${
        isCurrentUser ? "bg-orange-50" : "bg-white"
      }`}
    >
      <span className={`text-center text-sm font-extrabold ${rank <= 3 ? "text-orange-500" : "text-slate-400"}`}>
        {rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
      </span>
      <ProfilePicture userID={entry.uid} name={entry.displayName} className=" !h-9 !w-9 text-xs" />
      <span className="min-w-0 truncate text-sm font-bold text-slate-700">
        {entry.displayName}{isCurrentUser ? " (You)" : ""}
      </span>
      <span className="font-mono text-sm font-extrabold text-slate-900">
        {(metric === "movesFinishes" || metric === "timeFinishes") ? `${entry[metric]} ${entry[metric] === 1 ? "time" : "times"}` : metric === "moves" ? `${entry.moves} moves` : formatElapsed(entry.elapsedSeconds)}
      </span>
    </Component>
  );
}

